import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle, XCircle, Clock, AlertCircle, RefreshCw } from "lucide-react";
import { BillingService, Bill, ConfirmPaymentResponse } from "../services/billingService";

const BillPaymentConfirmation = () => {
  const { billId } = useParams<{ billId: string }>();
  const navigate = useNavigate();

  const { data: confirmation, isLoading, error, refetch } = useQuery({
    queryKey: ["bill-confirmation", billId],
    queryFn: () => billId ? BillingService.confirmPayment(billId) : Promise.reject("No bill ID"),
    enabled: !!billId,
    retry: false,
  });

  const getStatusConfig = (status: ConfirmPaymentResponse["status"]) => {
    switch (status) {
      case "PAID":
        return {
          icon: CheckCircle,
          iconColor: "text-green-500",
          bgColor: "bg-green-50",
          borderColor: "border-green-200",
          title: "Payment Successful!",
          message: "Your bill has been paid successfully. Thank you for your payment.",
          buttonText: "Back to Billing",
          buttonColor: "bg-green-600 hover:bg-green-700",
          showRetry: false,
        };
      case "PENDING":
        return {
          icon: Clock,
          iconColor: "text-yellow-500",
          bgColor: "bg-yellow-50",
          borderColor: "border-yellow-200",
          title: "Payment Processing",
          message: "Your payment is being processed. It may take a few minutes to reflect in your account.",
          buttonText: "Back to Billing",
          buttonColor: "bg-blue-600 hover:bg-blue-700",
          showRetry: true,
        };
      case "FAILED":
        return {
          icon: XCircle,
          iconColor: "text-red-500",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
          title: "Payment Failed",
          message: "Your payment could not be processed. Please try again or contact support.",
          buttonText: "Back to Billing",
          buttonColor: "bg-red-600 hover:bg-red-700",
          showRetry: false,
        };
      default:
        return {
          icon: AlertCircle,
          iconColor: "text-gray-500",
          bgColor: "bg-gray-50",
          borderColor: "border-gray-200",
          title: "Unknown Status",
          message: "Unable to determine payment status. Please contact support.",
          buttonText: "Back to Billing",
          buttonColor: "bg-gray-600 hover:bg-gray-700",
          showRetry: false,
        };
    }
  };

  const handleBackToBilling = () => {
    navigate("/billing");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Confirming Payment</h2>
          <p className="text-gray-600">Please wait while we verify your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Confirmation Failed</h2>
          <p className="text-gray-600 mb-6">
            {error instanceof Error ? error.message : "Unable to confirm payment status. Please try again."}
          </p>
          <button
            onClick={handleBackToBilling}
            className="w-full bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 font-medium"
          >
            Back to Billing
          </button>
        </div>
      </div>
    );
  }

  if (!confirmation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <AlertCircle className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Confirmation Not Found</h2>
          <p className="text-gray-600 mb-6">Unable to find the payment confirmation information.</p>
          <button
            onClick={handleBackToBilling}
            className="w-full bg-gray-600 text-white py-3 px-4 rounded-lg hover:bg-gray-700 font-medium"
          >
            Back to Billing
          </button>
        </div>
      </div>
    );
  }

  const config = getStatusConfig(confirmation.status);
  const Icon = config.icon;


  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Main Status Card */}
        <div className={`bg-white rounded-lg shadow-sm border ${config.borderColor} p-8 text-center mb-6`}>
          <div className={`w-16 h-16 ${config.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <Icon className={`w-8 h-8 ${config.iconColor}`} />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">{config.title}</h2>
          <p className="text-gray-600 mb-6">{config.message}</p>

          <div className="space-y-3">
            {config.showRetry && (
              <button
                onClick={() => refetch()}
                disabled={isLoading}
                className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border border-white border-t-transparent mr-2"></div>
                    Checking...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Check Payment Status
                  </>
                )}
              </button>
            )}

            <button
              onClick={handleBackToBilling}
              className={`w-full text-white py-3 px-4 rounded-lg font-medium ${config.buttonColor}`}
            >
              {config.buttonText}
            </button>
          </div>
        </div>

        {/* Payment Details Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h3>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Bill ID:</span>
              <span className="font-medium text-gray-900">{confirmation.billId}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Amount:</span>
              <span className="font-medium text-gray-900">₹{confirmation.amount.toLocaleString('en-IN')}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${confirmation.status === 'PAID' ? 'bg-green-100 text-green-800' :
                  confirmation.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                    confirmation.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                }`}>
                {confirmation.status}
              </span>
            </div>

            {confirmation.message && (
              <div className="pt-3 border-t border-gray-100">
                <span className="text-gray-600 text-xs">Message:</span>
                <p className="text-gray-900 text-sm mt-1">{confirmation.message}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillPaymentConfirmation;
